'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { DollarSign, Heart, Loader2, MessageSquare, Music, Zap } from 'lucide-react'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { trpc } from '@/lib/trpc'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'
import { getStripePromise } from '@/hooks/use-stripe-preload'

// Get Stripe from the preload hook (ensures single instance)
const stripePromise = getStripePromise()

// Default tip amounts in cents (fallback if no tipAmounts prop provided)
const DEFAULT_TIP_AMOUNTS = [200, 500, 1000, 2000] // $2, $5, $10, $20

// Format cents to display label
function formatTipLabel(cents: number): string {
  const dollars = cents / 100
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

// Queue position info for calculating dynamic tip amounts
export interface QueuePositionInfo {
  topTipAmountCents: number | null  // Tip amount of #1 request (null if no tipped requests)
  secondTipAmountCents: number | null  // Tip amount of #2 request (null if less than 2 tipped requests)
  totalPendingRequests: number  // Total number of pending requests in queue
  // For boosting existing requests - exclude this request from position calculations
  currentRequestId?: string  // The request being boosted (to exclude from calculations)
  currentRequestTipsCents?: number  // Existing tip amount on the request being boosted
}

interface TipSheetProps {
  isOpen: boolean
  onClose: () => void
  onPaymentSuccess?: () => void  // Called after successful payment (before onClose)
  artistName: string
  artistId: string
  eventId?: string        // Optional - for direct artist tips outside of events
  visitorId: string
  requestId?: string  // For adding tip to existing request (song or segment)
  songId?: string         // For creating NEW request with tip
  songTitle?: string
  initialAmount?: number
  // Tip amounts in cents from event settings
  tipAmounts?: number[]
  // Pre-created payment intents for all tip amounts (keyed by amount in cents)
  prewarmedIntents?: Map<number, { clientSecret: string; paymentIntentId: string; paymentId?: string }>
  // Whether the prewarm used DB fallback (true = DB record exists, false = cache only)
  prewarmedFallbackUsed?: boolean
  // Queue position info for calculating dynamic "beat position" amounts
  queuePositionInfo?: QueuePositionInfo
  // Whether tip messages are enabled for this event
  tipMessagesEnabled?: boolean
  // Minimum tip amount (cents) to allow a message (0 = no minimum)
  tipMessageMinimumCents?: number
  // Pre-populate message when boosting own request
  initialMessage?: string
  // Terminology-aware label for "song" (e.g., "bit", "scene", "trick") — defaults to "song"
  segmentLabel?: string
}

// Calculate dynamic "beat position" tip amounts
// When boosting an existing request, we need to:
// 1. Exclude that request from position calculations (don't compete with yourself)
// 2. Account for existing boosts on the request (user only needs to add the difference)
function calculateBeatPositionAmounts(
  baseAmounts: number[],
  queueInfo?: QueuePositionInfo
): { amount: number; label?: string }[] {
  const result: { amount: number; label?: string }[] = baseAmounts.map(a => ({ amount: a }))
  
  if (!queueInfo) return result
  
  const { topTipAmountCents, secondTipAmountCents, currentRequestTipsCents } = queueInfo
  const existingBoost = currentRequestTipsCents || 0
  
  // Calculate beat #2 amount
  // Need to beat #2's amount, accounting for any existing boost on this request
  if (secondTipAmountCents !== null && secondTipAmountCents > 0) {
    // To beat #2, total needs to be secondTipAmountCents + 100
    // User only needs to add: (secondTipAmountCents + 100) - existingBoost
    const additionalNeeded = (secondTipAmountCents + 100) - existingBoost
    // Only show if user needs to add at least $1
    if (additionalNeeded >= 100 && !baseAmounts.includes(additionalNeeded)) {
      result.push({ amount: additionalNeeded, label: 'Take #2' })
    }
  }
  
  // Calculate beat #1 amount
  // Need to beat #1's amount, accounting for any existing boost on this request
  if (topTipAmountCents !== null && topTipAmountCents > 0) {
    // To beat #1, total needs to be topTipAmountCents + 100
    // User only needs to add: (topTipAmountCents + 100) - existingBoost
    const additionalNeeded = (topTipAmountCents + 100) - existingBoost
    // Only show if user needs to add at least $1 and it's different from Take #2
    if (additionalNeeded >= 100 && !baseAmounts.includes(additionalNeeded)) {
      const beatSecondNeeded = secondTipAmountCents !== null 
        ? (secondTipAmountCents + 100) - existingBoost 
        : null
      if (beatSecondNeeded === null || additionalNeeded !== beatSecondNeeded) {
        result.push({ amount: additionalNeeded, label: 'Take #1' })
      }
    }
  }
  
  // Sort by amount
  result.sort((a, b) => a.amount - b.amount)
  
  return result
}

// Inner component that handles payment intent creation and Elements rendering
function TipSheetInner({
  requestId,
  songId,
  songTitle,
  initialAmount,
  onClose,
  onPaymentSuccess,
  eventId,
  artistId,
  visitorId,
  tipAmounts,
  prewarmedIntents,
  prewarmedFallbackUsed,
  queuePositionInfo,
  tipMessagesEnabled,
  tipMessageMinimumCents = 500,
  initialMessage,
  segmentLabel = 'song',
}: Omit<TipSheetProps, 'isOpen' | 'artistName'>) {
  const t = useTranslations('tips')
  // Use provided tip amounts or defaults
  const baseAmounts = tipAmounts && tipAmounts.length > 0 ? tipAmounts : DEFAULT_TIP_AMOUNTS
  
  // Calculate amounts including dynamic "beat position" options
  const amountsWithLabels = calculateBeatPositionAmounts(baseAmounts, queuePositionInfo)
  const _amounts = amountsWithLabels.map(a => a.amount)
  
  const defaultInitialAmount = initialAmount ?? baseAmounts[Math.min(1, baseAmounts.length - 1)] // Default to 2nd option or first
  const [selectedAmount, setSelectedAmount] = useState(defaultInitialAmount)
  const [customAmount, setCustomAmount] = useState('')
  const [isCustom, setIsCustom] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null)
  const [isConditional, setIsConditional] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [intentCreated, setIntentCreated] = useState(false)
  const [tipMessage, setTipMessage] = useState(initialMessage || '')
  // Debounced custom amount — only used for triggering payment intent creation
  const [debouncedCustomAmount, setDebouncedCustomAmount] = useState('')
  const customAmountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Get current display amount (updates immediately for UI)
  const currentDisplayAmount = isCustom && customAmount
    ? Math.round(parseFloat(customAmount) * 100) || 0
    : selectedAmount

  // Get the amount used for payment intent creation (debounced for custom)
  const currentAmount = isCustom && debouncedCustomAmount
    ? Math.round(parseFloat(debouncedCustomAmount) * 100) || 0
    : selectedAmount

  // Determine which flow we're in
  const isNewRequestWithTip = !!songId && !requestId
  const isAddingToExistingRequest = !!requestId
  const isDirectArtistTip = !eventId && !requestId && !songId
  const isEventTip = !!eventId && !songId && !requestId

  // For direct tips to artist (outside of events)
  const createDirectArtistTip = trpc.payment.createDirectArtistTip.useMutation({
    onSuccess: (data) => {
      setClientSecret(data.clientSecret)
      setPaymentIntentId(data.paymentIntentId)
      setIsConditional(false)
    },
    onError: (error) => {
      setErrorMessage(error.message)
      setIntentCreated(false)
    },
  })

  // For direct tips during events
  const createTipIntent = trpc.payment.createTipIntent.useMutation({
    onSuccess: (data) => {
      setClientSecret(data.clientSecret)
      setPaymentIntentId(data.paymentIntentId)
      setIsConditional(data.isConditional)
    },
    onError: (error) => {
      setErrorMessage(error.message)
      setIntentCreated(false)
    },
  })

  // For adding tip to existing request
  const addTipToRequest = trpc.payment.addTipToRequest.useMutation({
    onSuccess: (data) => {
      setClientSecret(data.clientSecret)
      setPaymentIntentId(data.paymentIntentId)
      setIsConditional(true)
    },
    onError: (error) => {
      setErrorMessage(error.message)
      setIntentCreated(false)
    },
  })

  // For creating NEW request with tip attached
  const createRequestWithTip = trpc.payment.createRequestWithTip.useMutation({
    onSuccess: (data) => {
      setClientSecret(data.clientSecret)
      setPaymentIntentId(data.paymentIntentId)
      setIsConditional(true)
    },
    onError: (error) => {
      setErrorMessage(error.message)
      setIntentCreated(false)
    },
  })

  const isLoading = createDirectArtistTip.isPending || createTipIntent.isPending || addTipToRequest.isPending || createRequestWithTip.isPending

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (customAmountTimerRef.current) {
        clearTimeout(customAmountTimerRef.current)
      }
    }
  }, [])

  // Check if we have a prewarmed intent for the current amount
  // Can use prewarmed intents for ALL tip types now (event tips, direct artist tips, song requests, boosts)
  const prewarmedForAmount = prewarmedIntents?.get(currentAmount)
  const canUsePrewarmed = !!prewarmedForAmount

  // Use prewarmed intent instantly when available for current amount
  useEffect(() => {
    if (canUsePrewarmed && prewarmedForAmount) {
      setClientSecret(prewarmedForAmount.clientSecret)
      setPaymentIntentId(prewarmedForAmount.paymentIntentId)
      // Song requests (boost or new) are conditional tips
      setIsConditional(isAddingToExistingRequest || isNewRequestWithTip)
      setIntentCreated(true)
      setErrorMessage(null)
    }
  }, [canUsePrewarmed, prewarmedForAmount, isAddingToExistingRequest, isNewRequestWithTip])

  // Create payment intent only if no prewarmed intent available
  // currentAmount is already debounced for custom input, so no additional delay needed
  useEffect(() => {
    if (currentAmount < 100) return
    if (canUsePrewarmed) return // Don't create, we have a prewarmed one
    if (intentCreated || clientSecret) return
    if (isLoading) return

    setErrorMessage(null)
    setIntentCreated(true)

    // Compute canSendMessage once for all flows
    // tipMessagesEnabled defaults to true when undefined (profile tips always allow messages)
    const canSendMessage = (tipMessagesEnabled !== false) && currentAmount >= tipMessageMinimumCents && !!tipMessage.trim()

    if (isDirectArtistTip) {
      // Direct tip to artist outside of events
      createDirectArtistTip.mutate({
        artistId,
        visitorId,
        amountCents: currentAmount,
        ...(canSendMessage ? { message: tipMessage.trim() } : {}),
      })
    } else if (isNewRequestWithTip) {
      createRequestWithTip.mutate({
        eventId: eventId!,
        songId: songId!,
        visitorId,
        amountCents: currentAmount,
        ...(canSendMessage ? { message: tipMessage.trim() } : {}),
      })
    } else if (isAddingToExistingRequest) {
      addTipToRequest.mutate({
        requestId: requestId!,
        visitorId,
        amountCents: currentAmount,
        ...(canSendMessage ? { message: tipMessage.trim() } : {}),
      })
    } else {
      createTipIntent.mutate({
        eventId: eventId!,
        visitorId,
        amountCents: currentAmount,
        ...(canSendMessage ? { message: tipMessage.trim() } : {}),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAmount, intentCreated, clientSecret, isLoading, canUsePrewarmed, isDirectArtistTip, isNewRequestWithTip, isAddingToExistingRequest])

  const handleSelectPreset = (amount: number) => {
    // Clear any pending custom amount debounce
    if (customAmountTimerRef.current) {
      clearTimeout(customAmountTimerRef.current)
      customAmountTimerRef.current = null
    }
    if (amount !== selectedAmount || isCustom) {
      // Only reset if we don't have a prewarmed intent for this amount
      // Prewarmed intents work for ALL tip types now
      const hasPrewarmed = prewarmedIntents?.has(amount)
      if (!hasPrewarmed) {
        setIntentCreated(false)
        setClientSecret(null)
        setPaymentIntentId(null)
      }
    }
    setSelectedAmount(amount)
    setIsCustom(false)
    setCustomAmount('')
    setDebouncedCustomAmount('')
  }

  const handleCustomAmountChange = useCallback((value: string) => {
    const cleaned = value.replace(/[^0-9.]/g, '')
    setCustomAmount(cleaned)
    setIsCustom(true)

    // Debounce the payment intent reset — wait for user to finish typing
    if (customAmountTimerRef.current) {
      clearTimeout(customAmountTimerRef.current)
    }
    customAmountTimerRef.current = setTimeout(() => {
      setDebouncedCustomAmount(cleaned)
      // Only reset payment state if the amount actually changed
      setIntentCreated(false)
      setClientSecret(null)
      setPaymentIntentId(null)
    }, 600)
  }, [])

  // Determine button state
  const showPaymentForm = clientSecret && paymentIntentId && stripePromise
  const displayAmount = currentDisplayAmount / 100

  // Render amount selection (shared between both payment and non-payment views)
  const amountSelectionContent = (
    <>
      {/* Song info if tipping a request */}
      {songTitle && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-violet-500/10 border border-violet-500/30">
          <Music className="h-4 w-4 text-violet-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-violet-300">{t('tippingFor')}</p>
            <p className="text-sm font-medium text-white truncate">{songTitle}</p>
          </div>
        </div>
      )}

      {/* Jump the line prompt - shown when queue has requests but no tips yet */}
      {queuePositionInfo && 
       queuePositionInfo.totalPendingRequests > 0 && 
       queuePositionInfo.topTipAmountCents === null && 
       (requestId || songId) && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <Zap className="h-4 w-4 text-amber-400 shrink-0" />
          <p className="text-xs text-amber-300">
            <span className="font-medium">{t('jumpTheLine')}</span>{' '}
            {queuePositionInfo.totalPendingRequests === 1
              ? t('jumpTheLineOne', { segment: segmentLabel })
              : t('jumpTheLineMany', { count: queuePositionInfo.totalPendingRequests, segments: `${segmentLabel}s` })
            }
          </p>
        </div>
      )}

      {/* Amount selection - always visible */}
      <div className="space-y-2">
        {/* Base amounts */}
        <div className={`grid gap-2 ${baseAmounts.length <= 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
          {baseAmounts.map((amount) => (
            <Button
              key={amount}
              type="button"
              variant={selectedAmount === amount && !isCustom ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSelectPreset(amount)}
              className={
                selectedAmount === amount && !isCustom
                  ? 'bg-green-600 hover:bg-green-700 text-white h-9'
                  : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-9'
              }
            >
              {formatTipLabel(amount)}
            </Button>
          ))}
        </div>
        
        {/* Dynamic "beat position" amounts - only show for song requests, not regular tips */}
        {(isNewRequestWithTip || isAddingToExistingRequest) && amountsWithLabels.filter(a => a.label).length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs text-zinc-400">
              {t('jumpAheadInQueue')}
            </p>
            <div className="flex gap-2">
              {amountsWithLabels.filter(a => a.label).map(({ amount, label }) => (
                <Button
                  key={amount}
                  type="button"
                  variant={selectedAmount === amount && !isCustom ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleSelectPreset(amount)}
                  className={
                    selectedAmount === amount && !isCustom
                      ? 'bg-amber-600 hover:bg-amber-700 text-white h-9 flex-1'
                      : 'border-amber-500/50 text-amber-300 hover:bg-amber-500/20 h-9 flex-1'
                  }
                >
                  <span className="flex flex-col items-center leading-tight">
                    <span className="text-xs opacity-75">{label}</span>
                    <span>{formatTipLabel(amount)}</span>
                  </span>
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            type="text"
            inputMode="decimal"
            placeholder={t('otherAmount')}
            value={customAmount}
            onChange={(e) => handleCustomAmountChange(e.target.value)}
            className="pl-9 bg-zinc-800 border-zinc-700 text-white h-9"
          />
        </div>
      </div>

      {/* Tip message input — shown for all tip types when enabled and meets minimum */}
      {(tipMessagesEnabled !== false) && (isNewRequestWithTip || isAddingToExistingRequest || isEventTip || isDirectArtistTip) && (() => {
        const meetsMinimum = currentDisplayAmount >= tipMessageMinimumCents
        const isRequestFlow = isNewRequestWithTip || isAddingToExistingRequest
        return meetsMinimum ? (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs text-zinc-400">{t('addMessage')}</label>
              <span className={`text-xs ${tipMessage.length > 130 ? 'text-amber-400' : 'text-zinc-500'}`}>
                {tipMessage.length}/140
              </span>
            </div>
            <textarea
              value={tipMessage}
              onChange={(e) => {
                if (e.target.value.length <= 140) {
                  setTipMessage(e.target.value)
                }
              }}
              placeholder={isRequestFlow ? t('messagePlaceholderRequest') : t('messagePlaceholderGeneral')}
              className="w-full px-3 py-2 text-sm bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 resize-none focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500"
              rows={2}
            />
            <p className="text-xs text-zinc-500">{t('messagesModerated')}</p>
          </div>
        ) : tipMessageMinimumCents > 0 ? (
          <p className="text-xs text-zinc-500 flex items-center gap-1.5">
            <MessageSquare className="h-3 w-3" />
            {t('tipMinimumForMessage', { amount: `$${(tipMessageMinimumCents / 100).toFixed(tipMessageMinimumCents % 100 === 0 ? 0 : 2)}` })}
          </p>
        ) : null
      })()}

      {/* Error message */}
      {errorMessage && (
        <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {errorMessage}
        </div>
      )}
    </>
  )

  // Check if we're using a prewarmed intent from cache (not DB fallback)
  const prewarmedForCurrentAmount = prewarmedIntents?.get(currentAmount)
  const needsMaterialization = prewarmedForCurrentAmount && !prewarmedForCurrentAmount.paymentId && !prewarmedFallbackUsed

  // If payment form is ready, wrap everything in Elements provider
  if (showPaymentForm) {
    return (
      <Elements
        key={clientSecret} // Force remount when clientSecret changes
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: 'night',
            variables: {
              colorPrimary: '#22c55e',
              colorBackground: '#27272a',
              colorText: '#ffffff',
              colorDanger: '#ef4444',
              borderRadius: '6px',
              fontFamily: 'system-ui, sans-serif',
              spacingUnit: '3px',
            },
          },
        }}
      >
        <PaymentFormWithAmounts
          amountSelectionContent={amountSelectionContent}
          currentAmount={currentAmount}
          isConditional={isConditional}
          requestId={requestId}
          songId={songId}
          paymentIntentId={paymentIntentId!}
          message={(tipMessagesEnabled !== false) && currentAmount >= tipMessageMinimumCents && tipMessage.trim() ? tipMessage.trim() : undefined}
          onSuccess={onPaymentSuccess || (() => {})}
          onClose={onClose}
          // Materialization props for cache-first flow
          needsMaterialization={needsMaterialization}
          eventId={eventId}
          artistId={artistId}
          visitorId={visitorId}
          segmentLabel={segmentLabel}
        />
      </Elements>
    )
  }

  // No payment form yet - show amounts only with loading/disabled state
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto min-h-0 space-y-3">
        {amountSelectionContent}
        
        {/* Loading or select amount message */}
        {isLoading ? (
          <div className="py-6 flex items-center justify-center text-zinc-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span>{t('settingUpPayment')}</span>
          </div>
        ) : currentDisplayAmount < 100 ? (
          <div className="py-6 flex items-center justify-center text-zinc-500 text-sm">
            {t('selectAmountToContinue')}
          </div>
        ) : null}
      </div>

      {/* Fixed action buttons at bottom */}
      <div className="shrink-0 flex gap-3 pt-3 border-t border-zinc-800 mt-3">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="flex-1 border-zinc-700"
          disabled={isLoading}
        >
          {t('cancel')}
        </Button>
        <Button
          type="button"
          disabled
          className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600"
        >
          <Heart className="mr-2 h-4 w-4" />
          {requestId || songId ? t('authorize') : t('send')} ${displayAmount.toFixed(2)}
        </Button>
      </div>
    </div>
  )
}

// Combined payment form with amount selection visible - used inside Elements provider
function PaymentFormWithAmounts({
  amountSelectionContent,
  currentAmount,
  isConditional,
  requestId,
  songId,
  paymentIntentId,
  message,
  onSuccess,
  onClose,
  // Materialization props for cache-first flow
  needsMaterialization,
  eventId,
  artistId,
  visitorId,
  segmentLabel = 'song',
}: {
  amountSelectionContent: React.ReactNode
  currentAmount: number
  isConditional: boolean
  requestId?: string
  songId?: string
  paymentIntentId: string
  message?: string
  onSuccess: () => void
  onClose: () => void
  // Materialization props
  needsMaterialization?: boolean
  eventId?: string
  artistId: string
  visitorId: string
  segmentLabel?: string
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [activeClientSecret, setActiveClientSecret] = useState<string | null>(null)
  const t = useTranslations('tips')

  const confirmPayment = trpc.payment.confirmPaymentSucceeded.useMutation()
  const materializeTipRecord = trpc.payment.materializeTipRecord.useMutation()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isProcessing || !stripe || !elements) return

    setIsProcessing(true)
    setErrorMessage(null)

    try {
      // First, validate and submit the payment element to ensure it has captured payment details
      const { error: submitError } = await elements.submit()
      if (submitError) {
        setErrorMessage(submitError.message || t('checkPaymentDetails'))
        setIsProcessing(false)
        return
      }

      // If using cache-first prewarm, materialize the DB record before confirming payment
      // This creates the Payment/Tip records in the database
      let _finalPaymentIntentId = paymentIntentId
      let _finalClientSecret = activeClientSecret

      if (needsMaterialization) {
        try {
          const materializeResult = await materializeTipRecord.mutateAsync({
            paymentIntentId,
            eventId,
            artistId,
            visitorId,
            amountCents: currentAmount,
          })

          // If JIT created a new PaymentIntent (cache expired), we need to use the new one
          if (materializeResult.needsNewIntent && materializeResult.clientSecret) {
            console.log('[TipSheet] JIT created new PaymentIntent, updating...')
            _finalPaymentIntentId = paymentIntentId // Keep original for now, materialize returns same ID
            _finalClientSecret = materializeResult.clientSecret
            setActiveClientSecret(materializeResult.clientSecret)
            
            // We need to reinitialize Elements with the new clientSecret
            // For now, show an error asking user to retry
            setErrorMessage(t('paymentSessionExpired'))
            setIsProcessing(false)
            return
          }
        } catch (materializeError) {
          console.error('Failed to materialize tip record:', materializeError)
          setErrorMessage(t('failedToPreparePayment'))
          setIsProcessing(false)
          return
        }
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      })

      if (error) {
        setErrorMessage(error.message || t('paymentFailed'))
        setIsProcessing(false)
        return
      }

      const validStatuses = ['requires_capture', 'succeeded', 'processing']
      if (!paymentIntent || !validStatuses.includes(paymentIntent.status)) {
        setErrorMessage(
          paymentIntent?.status === 'requires_payment_method'
            ? t('enterPaymentDetails')
            : t('paymentCouldNotBeProcessed', { status: paymentIntent?.status || 'unknown' })
        )
        setIsProcessing(false)
        return
      }

      try {
        await confirmPayment.mutateAsync({
          paymentIntentId: paymentIntent.id,
          ...(message?.trim() ? { message: message.trim() } : {}),
        })
      } catch (e) {
        console.error('Could not confirm payment status:', e)
      }

      if (isConditional) {
        toast.success(t('boostAuthorized', { segment: segmentLabel }) + ' 🎵')
      } else {
        toast.success(t('thankYouForTip') + ' 🎉')
      }
      onSuccess()
      onClose()
    } catch (err) {
      console.error('Payment error:', err)
      setErrorMessage(t('unexpectedError'))
      setIsProcessing(false)
    }
  }

  const displayAmount = currentAmount / 100

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0">
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto min-h-0 space-y-3">
        {/* Amount selection content passed from parent */}
        {amountSelectionContent}

        {/* Payment element */}
        <div className="rounded-lg overflow-hidden">
          <PaymentElement
            onReady={(element) => element.focus()}
            options={{
              layout: 'accordion',
              wallets: { applePay: 'auto', googlePay: 'auto' },
            }}
          />
        </div>

        {/* Payment error */}
        {errorMessage && (
          <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {errorMessage}
          </div>
        )}
      </div>

      {/* Fixed action buttons at bottom */}
      <div className="shrink-0 flex gap-3 pt-3 border-t border-zinc-800 mt-3">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="flex-1 border-zinc-700"
          disabled={isProcessing}
        >
          {t('cancel')}
        </Button>
        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('processing')}
            </>
          ) : (
            <>
              <Heart className="mr-2 h-4 w-4" />
              {requestId || songId ? t('authorize') : t('send')} ${displayAmount.toFixed(2)}
            </>
          )}
        </Button>
      </div>
    </form>
  )
}

export function TipSheet({
  isOpen,
  onClose,
  onPaymentSuccess,
  artistName,
  artistId,
  eventId,
  visitorId,
  requestId,
  songId,
  songTitle,
  initialAmount,
  tipAmounts,
  prewarmedIntents,
  prewarmedFallbackUsed,
  queuePositionInfo,
  tipMessagesEnabled,
  tipMessageMinimumCents,
  initialMessage,
  segmentLabel,
}: TipSheetProps) {
  const t = useTranslations('tips')
  const [key, setKey] = useState(0)

  // Reset form when sheet opens
  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Reset form key when opening
      setKey((k) => k + 1)
    }
  }, [isOpen])

  if (!stripePromise) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="bottom" className="bg-zinc-900 border-zinc-700">
          <SheetHeader>
            <SheetTitle className="text-white">{t('sendATip')}</SheetTitle>
          </SheetHeader>
          <div className="py-8 text-center">
            <p className="text-red-400">{t('paymentNotConfigured')}</p>
            <p className="text-xs text-zinc-500 mt-1">{t('contactVenue')}</p>
          </div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="bottom" 
        className="bg-zinc-900 border-zinc-700 h-[65dvh] max-h-[600px] flex flex-col p-0"
      >
        <SheetHeader className="px-4 pt-4 pb-2 shrink-0">
          <SheetTitle className="text-white flex items-center gap-2 text-base">
            <Heart className="h-4 w-4 text-pink-400" />
            {songId ? t('requestWithTip') : requestId ? t('boostRequest') : t('sendATip')}
          </SheetTitle>
          <SheetDescription className="text-zinc-400 text-sm">
            {songId
              ? t('onlyChargedIfPlayed', { segment: segmentLabel || 'song' })
              : requestId
                ? t('boostOnlyChargedIfPlayed')
                : t('showAppreciation', { artistName })
            }
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-hidden px-4 pb-4 flex flex-col min-h-0">
          <TipSheetInner
            key={key}
            artistId={artistId}
            eventId={eventId}
            visitorId={visitorId}
            requestId={requestId}
            songId={songId}
            songTitle={songTitle}
            initialAmount={initialAmount}
            tipAmounts={tipAmounts}
            onClose={onClose}
            onPaymentSuccess={onPaymentSuccess}
            prewarmedIntents={prewarmedIntents}
            prewarmedFallbackUsed={prewarmedFallbackUsed}
            queuePositionInfo={queuePositionInfo}
            tipMessagesEnabled={tipMessagesEnabled}
            tipMessageMinimumCents={tipMessageMinimumCents}
            initialMessage={initialMessage}
            segmentLabel={segmentLabel}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}
